/**
 * Bundle SKU Picker - extends SkuPicker for Shopify Bundles
 *
 * Applies same configuration (size/frame/border) to all bundle items.
 * All bundle components share the same configurator and variant.
 * Price is multiplied by total bundle item count.
 *
 * Uses Shopify Bundles app metafield structure:
 * product.metafields.bundles.bundle_components
 *
 * Note: This file is loaded after sku-picker.js, so SkuPicker is available
 */

class BundleSkuPicker extends SkuPicker {
  constructor(containerId) {
    super(containerId);
    this.bundleComponents = this._loadBundleComponents();
    this.totalBundleItems = this._calculateTotalItems();

    console.log('[BundleSkuPicker] Initialized with', this.totalBundleItems, 'total items');
  }

  /**
   * Load bundle component data from embedded script tag
   * @returns {Array|null} Array of bundle components or null if not found
   */
  _loadBundleComponents() {
    const dataElement = document.getElementById('BundleComponentData');
    if (!dataElement) {
      console.error('[BundleSkuPicker] No bundle component data found');
      return null;
    }

    try {
      const data = JSON.parse(dataElement.textContent);
      console.log('[BundleSkuPicker] Loaded bundle components:', data.components);
      return data.components;
    } catch (error) {
      console.error('[BundleSkuPicker] Failed to parse bundle component data:', error);
      return null;
    }
  }

  /**
   * Calculate total items in bundle (sum of component quantities)
   * @returns {number} Total number of items across all components
   */
  _calculateTotalItems() {
    if (!this.bundleComponents || !Array.isArray(this.bundleComponents)) {
      return 1;
    }
    return this.bundleComponents.reduce((sum, component) => sum + component.quantity, 0);
  }

  /**
   * Override: Multiply price by bundle item count
   * @param {Object} selection - The current selection state
   * @param {string} variantId - The variant ID
   * @returns {Object} Selection details with bundle pricing
   */
  _getSelectionDetails(selection, variantId) {
    // Call parent method to get base price for single item
    const details = super._getSelectionDetails(selection, variantId);

    if (!details.available) {
      return details;
    }

    // Multiply by bundle item count
    const bundlePrice = details.price * this.totalBundleItems;
    const bundleComparePrice = details.compareAtPrice
      ? details.compareAtPrice * this.totalBundleItems
      : null;

    return {
      ...details,
      price: bundlePrice,
      compareAtPrice: bundleComparePrice,
      basePrice: details.price, // Store original per-item price
      bundleItemCount: this.totalBundleItems
    };
  }

  /**
   * Override: Render bundle-specific price display
   * Shows total bundle price with item count breakdown
   * @param {HTMLElement} priceContainer - Container element for price display
   * @param {Object} details - Selection details from _getSelectionDetails
   */
  _renderPriceDisplay(priceContainer, details) {
    if (!details.available) {
      return super._renderPriceDisplay(priceContainer, details);
    }

    const priceHTML = `
      <div class="sku-picker__bundle-price">
        <div class="sku-picker__price-main">
          ${this._formatTotalPrice(details.price)}
        </div>
        <div class="sku-picker__bundle-info">
          Bundle Total (${this.totalBundleItems} items × ${this._formatTotalPrice(details.basePrice)})
        </div>
        ${details.compareAtPrice ? `
          <div class="sku-picker__price-compare">
            <s>${this._formatTotalPrice(details.compareAtPrice)}</s>
          </div>
        ` : ''}
      </div>
    `;

    priceContainer.innerHTML = priceHTML;
  }

  /**
   * Override: Prepare bundle submission data
   * Stores bundle component info and shared properties for product-form.js
   * @param {Object} selection - The current selection state
   * @param {Object} details - Selection details from _getSelectionDetails
   */
  _updateFormPropertiesForSelection(selection, details) {
    // Call parent to update form properties normally
    super._updateFormPropertiesForSelection(selection, details);

    if (!this.bundleComponents) {
      console.warn('[BundleSkuPicker] No bundle components available for submission');
      return;
    }

    // Build shared properties from form inputs
    const sharedProperties = this._extractPropertiesFromForm();

    // Store bundle submission data for product-form.js
    window._bundleSubmissionData = {
      bundleId: `bundle-${Date.now()}`,
      components: this.bundleComponents,
      sharedProperties: sharedProperties
    };

    console.log('[BundleSkuPicker] Prepared bundle submission:', {
      componentCount: this.bundleComponents.length,
      totalItems: this.totalBundleItems,
      properties: Object.keys(sharedProperties)
    });
  }

  /**
   * Helper: Extract properties from form inputs
   * Collects all properties[*] inputs from the product form
   * @returns {Object} Key-value map of properties
   */
  _extractPropertiesFromForm() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.warn('[BundleSkuPicker] Product form not found');
      return {};
    }

    const properties = {};

    // Collect all property inputs
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const match = input.name.match(/properties\[(.+?)\]/);
      if (match && input.value) {
        properties[match[1]] = input.value;
      }
    });

    return properties;
  }

  /**
   * Override: Show bundle-specific availability messaging
   * @param {HTMLElement} container - Container for availability message
   * @param {Object} details - Selection details from _getSelectionDetails
   */
  _renderAvailabilityMessage(container, details) {
    if (!details.available) {
      container.innerHTML = `
        <div class="sku-picker__unavailable">
          This bundle configuration is currently unavailable
        </div>
      `;
      return;
    }

    // Use parent implementation for available states
    super._renderAvailabilityMessage(container, details);
  }

  /**
   * Helper: Format price for display
   * Reuses parent method if available, otherwise implements basic formatting
   * @param {number} priceCents - Price in cents
   * @returns {string} Formatted price string
   */
  _formatTotalPrice(priceCents) {
    // Try to use parent implementation
    if (super._formatTotalPrice) {
      return super._formatTotalPrice(priceCents);
    }

    // Fallback: Basic formatting
    const dollars = (priceCents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BundleSkuPicker;
}

// Initialize immediately - no need to wait for DOMContentLoaded since script is deferred
// Deferred scripts already wait for DOM parsing to complete
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleSkuPicker);
} else {
  // DOM already loaded, initialize immediately
  initBundleSkuPicker();
}

function initBundleSkuPicker() {
  // Check if bundle component data exists
  const bundleData = document.getElementById('BundleComponentData');
  if (!bundleData) {
    console.warn('[BundleSkuPicker] No bundle data found, bundle picker not initialized');
    return;
  }

  console.log('[BundleSkuPicker] Initializing bundle SKU picker');
  window.skuPickerInstance = new BundleSkuPicker('sku-picker-container');
}
